import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  handleCallback,
  getStoredToken,
  getStoredUser,
  clearTokens,
  isSessionExpired,
  isSessionExpiringSoon,
  getSessionTimeRemaining,
  extendSession,
} from '../services/auth';
import { SESSION_CONFIG } from '../constants';
import { clearAllCache } from '../services/cache';
import type { GoogleUser } from '../types/auth';

interface AppContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  authError: string | null;
  user: GoogleUser | null;
  sessionExpiringSoon: boolean;
  sessionTimeRemaining: number;
  logout: () => void;
  extendUserSession: () => void;
}

const AppContext = createContext<AppContextType | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [user, setUser] = useState<GoogleUser | null>(null);
  const [sessionExpiringSoon, setSessionExpiringSoon] = useState(false);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(0);
  const callbackProcessedRef = useRef(false);
  const navigate = useNavigate();

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const isCallback =
        window.location.pathname === '/callback' || window.location.search.includes('code=');

      if (isCallback) {
        if (callbackProcessedRef.current) return;
        callbackProcessedRef.current = true;

        try {
          const success = await handleCallback();
          if (success) {
            setIsAuthenticated(true);
            setUser(getStoredUser());
          } else {
            clearTokens();
            setAuthError(
              'Login failed. Make sure you are using an encorestateplans.com Google account.'
            );
          }
        } catch (err) {
          clearTokens();
          setAuthError(err instanceof Error ? err.message : 'Login error');
        }
        navigate('/', { replace: true });
      } else {
        const token = getStoredToken();
        const sessionValid = !isSessionExpired();
        if (token && sessionValid) {
          setIsAuthenticated(true);
          setUser(getStoredUser());
        } else if (token) {
          clearTokens();
          setAuthError('Your session has expired. Please log in again.');
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  // Session monitoring
  useEffect(() => {
    if (!isAuthenticated) return;

    const check = () => {
      if (isSessionExpired()) {
        clearTokens();
        setIsAuthenticated(false);
        setAuthError('Your session has expired. Please log in again.');
        return;
      }
      setSessionExpiringSoon(isSessionExpiringSoon());
      setSessionTimeRemaining(getSessionTimeRemaining());
    };

    check();
    const id = setInterval(check, SESSION_CONFIG.CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isAuthenticated]);

  const logout = useCallback(() => {
    clearTokens();
    clearAllCache();
    setIsAuthenticated(false);
    setUser(null);
    setSessionExpiringSoon(false);
    setSessionTimeRemaining(0);
  }, []);

  const extendUserSession = useCallback(() => {
    extendSession();
    setSessionExpiringSoon(false);
    setSessionTimeRemaining(getSessionTimeRemaining());
  }, []);

  return (
    <AppContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        authError,
        user,
        sessionExpiringSoon,
        sessionTimeRemaining,
        logout,
        extendUserSession,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp(): AppContextType {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
